import { CustomTimeInput } from '@/src/components/common/CustomTimeInput/CustomTimeInput';
import { useUser } from '@/src/store/user.context';
import { IEventTemplate } from '@/src/types/EventTemplate';
import { isDateNotInThePast } from '@/src/utils/common.utils';
import {
  Alert,
  Checkbox,
  DatePicker,
  FormField,
  Input,
  Select,
  SpaceBetween,
  Tiles,
  TilesProps,
  TimeInput,
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MeridiemOptions } from '../../../../constants/DateTimeConstants';
import { EditEventActions, useEditEvent } from '../../../../store/edit-event.context';
import { NullableString } from '../../../../types/common';
import { Event, EventStatus, MAX_DURATION_NON_ADMIN } from '../../../../types/Event';
import {
  beforeNumBusinessDays,
  convert24To12HourTime,
  extractMeridiem,
  getBrowserTimezoneName,
  getTimeForPolarisTimePicker,
  getTimeInTimeZone,
  getTimezonesWithUTCOffsetAsOptionList,
  polarisDateTimeToMoment,
} from '../../../../utils/event-time.utils';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { getLanguageCodeSafe } from '../../../../utils/locale.utils';
import { containsOnlyNumbers, isEmpty } from '../../../../utils/string.utils';
import { KeyValue } from '../../../common/KeyValue';
import './EventTimes.scss';

interface EventTimesProps {
  target: Event | undefined;
  eventTemplate?: IEventTemplate | undefined;
  validatorRef?: React.MutableRefObject<() => boolean>;
  requesterEventsCount?: number;
  eventQuotaDaysPeriod?: number;
  eventQuotaLimit?: number;
  eventQuotaWarning?: number;
  eventQuotaSLADays?: number;
}

const EventTimes: React.FC<EventTimesProps> = ({
  target,
  eventTemplate,
  validatorRef,
  requesterEventsCount,
  eventQuotaDaysPeriod = 30,
  eventQuotaLimit = 30,
  eventQuotaWarning = 25,
  eventQuotaSLADays = 5,
}) => {
  const { i18n, t } = useTranslation();
  const { user } = useUser();
  const {
    editMode,
    newEventMode,
    handleUpdateEditEvent,
    newDuration,
    setNewDuration,
    selectedSpecificDuration,
    setSelectedSpecificDuration,
    getEventDurationInfo,
  } = useEditEvent();
  const [timezoneOptions, setTimezoneOptions] = useState<OptionDefinition[]>([]);
  const [selectedTimezoneOption, setSelectedTimezoneOption] = useState<OptionDefinition | null>(null);

  const [startDate, setStartDate] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [startDateUpdated, setStartDateUpdated] = useState(false);
  const [startBeforeEventApprovalSLA, setStartBeforeEventApprovalSLA] = useState<'ERROR' | 'WARNING' | null>(null);

  const [startTime, setStartTime] = useState('');
  const [startTimeError, setStartTimeError] = useState('');
  const [startTimeUpdated, setStartTimeUpdated] = useState(false);

  const [durationError, setDurationError] = useState('');
  const [durationUpdated, setDurationUpdated] = useState(false);

  const [selectedMeridiem, setSelectedMeridiem] = useState<OptionDefinition>(MeridiemOptions.AM);
  const [autoUnlockChallenges, setAutoUnlockChallenges] = useState(false);
  const [autoUnlockChallengesMinutes, setAutoUnlockChallengesMinutes] = useState('0');
  const [minParticipants, setMinParticipants] = useState<NullableString>(
    `${(target as Event)?.minExpectedParticipants}`
  );
  const [participants, setParticipants] = useState<NullableString>(`${(target as Event)?.maxExpectedParticipants}`);

  const languageCode: string = getLanguageCodeSafe(i18n.language);

  const isDisabled = user?.isOnlyBasicUser && !target?.hasOwnerPermission(user?.email);
  const afterStartTime = !!target?.isAfterStart && target.status !== EventStatus.NOT_STARTED;

  const tooSoonRef = useRef<HTMLDivElement>(null);
  const startMoment = useMemo(
    () =>
      polarisDateTimeToMoment(
        startDate,
        `${startTime} ${selectedMeridiem.value}`,
        selectedTimezoneOption?.value || getBrowserTimezoneName() || ''
      ),
    [startDate, startTime, selectedMeridiem, selectedTimezoneOption]
  );

  const durationItems: Record<string | number, TilesProps.TilesDefinition> = {
    4: {
      label: t(i18nKeys.events.eventDetails.labels.durationItems.fourHours),
      value: '4',
      disabled: isDisabled,
    },
    8: {
      label: t(i18nKeys.events.eventDetails.labels.durationItems.eightHours),
      value: '8',
      disabled: isDisabled,
    },
    specific: {
      label: t(i18nKeys.events.eventDetails.labels.specific),
      value: 'specific',
      disabled: isDisabled,
    },
  };

  useEffect(() => {
    if ((target as Event)?.catalogId !== null) {
      setMinParticipants(participants);
      handleUpdateEditEvent(EditEventActions.MIN_EXPECTED_PARTICIPANTS, participants);
    }
  }, [participants]);

  useEffect(() => {
    setupSelections();
  }, [editMode, newEventMode]);

  /**
   * Url template generator isnt catching default update to target until second time it renders,
   * watch target change here and verify we add default if target changes and it doesnt exist
   */
  useEffect(() => {
    if (newEventMode && !target?.timezone) {
      const defaultTimezone = getBrowserTimezoneName();
      handleUpdateEditEvent(EditEventActions.TIMEZONE, defaultTimezone);
    }
  }, [target]);

  const validateStartTime = (updated: boolean) => {
    if (!updated) {
      return true;
    }

    if (isEmpty(startTime)) {
      setStartTimeError(t(i18nKeys.eventTemplates.catalogDetails.startTime.error));
      return false;
    }

    if (!startDate) {
      setStartTimeError('');
      return false;
    }

    const meridiem = selectedMeridiem.value ?? 'AM';
    const timezone = selectedTimezoneOption?.value || getBrowserTimezoneName() || '';

    const startDateTime = moment.tz(`${startDate} ${startTime} ${meridiem}`, 'YYYY/MM/DD hh:mm A', timezone);
    const isInPast = startDateTime.isBefore(moment().tz(timezone));

    if (isInPast) {
      setStartTimeError(t(i18nKeys.eventTemplates.catalogDetails.startTime.minimum));
      return false;
    }

    setStartTimeError('');
    return true;
  };

  const validateStartDate = (updated: boolean) => {
    if (!updated) {
      return true;
    }
    setStartBeforeEventApprovalSLA(null);
    if (isEmpty(startDate)) {
      setStartDateError(t(i18nKeys.eventTemplates.catalogDetails.startDate.error));
      return false;
    }

    const dateRegex = /^\d+-\d+-\d+$/;
    if (!dateRegex.test(startDate)) {
      setStartDateError(t(i18nKeys.eventTemplates.catalogDetails.startDate.error));
      return false;
    }

    const timezone = selectedTimezoneOption?.value || getBrowserTimezoneName() || '';
    const nowMoment = moment().tz(timezone);

    if (startMoment?.isBefore(nowMoment, 'date')) {
      setStartDateError(t(i18nKeys.eventTemplates.catalogDetails.startDate.minimum));
      return false;
    }

    let showEventLimitWarning: 'WARNING' | null = null;
    if (startMoment && beforeNumBusinessDays(startMoment?.toDate(), eventQuotaSLADays)) {
      if (requesterEventsCount && requesterEventsCount > eventQuotaLimit) {
        setStartDateError(t(i18nKeys.events.eventDetails.messages.eventApprovalSLADateError, { eventQuotaSLADays }));
        setStartBeforeEventApprovalSLA('ERROR');
        return false;
      } else if (requesterEventsCount && requesterEventsCount >= eventQuotaWarning) {
        showEventLimitWarning = 'WARNING';
      }
    }
    setStartBeforeEventApprovalSLA(showEventLimitWarning);
    setStartDateError('');
    return true;
  };

  const validateDuration = (updated: boolean) => {
    if (!updated) {
      return true;
    }

    const durationValue = newDuration === 'specific' ? selectedSpecificDuration : newDuration;
    const duration = moment.duration(durationValue, 'hours');

    if (!duration || !duration.isValid()) {
      setDurationError(t(i18nKeys.newEvent.errors.duration));
      return false;
    }

    if (duration.asMinutes() === 0) {
      setDurationError(t(i18nKeys.newEvent.errors.duration));
      return false;
    }

    const maxDuration = moment.duration(MAX_DURATION_NON_ADMIN, 'hours');

    if (duration > maxDuration && !user?.isSuperAdmin) {
      setDurationError(t(i18nKeys.newEvent.errors.durationLimit, { bound: MAX_DURATION_NON_ADMIN }));
      return false;
    }

    setDurationError('');
    return true;
  };

  const validateFields = () => {
    const validStartDate = validateStartDate(startDateUpdated);
    const validStartTime = validateStartTime(startTimeUpdated);
    const validDuration = validateDuration(durationUpdated);

    return validStartDate && validStartTime && validDuration;
  };

  // this will prevent user to next step in wizard
  if (validatorRef) {
    validatorRef.current = () => {
      setStartDateUpdated(true);
      setStartTimeUpdated(true);
      setDurationUpdated(true);

      const validStartDate = validateStartDate(true);
      const validStartTime = validateStartTime(true);
      const validDuration = validateDuration(true);

      return validStartDate && validStartTime && validDuration;
    };
  }

  const setupSelections = () => {
    const newTimezoneOptions = getTimezonesWithUTCOffsetAsOptionList();
    setTimezoneOptions(newTimezoneOptions);
    const defaultTimezone = target?.timezone ? target.timezone : getBrowserTimezoneName();

    if (!target?.timezone) {
      handleUpdateEditEvent(EditEventActions.TIMEZONE, defaultTimezone);
    }

    if (defaultTimezone) {
      const selectedTimezone = newTimezoneOptions.find((e) => e.value === defaultTimezone);

      if (selectedTimezone) {
        setSelectedTimezoneOption(selectedTimezone);
      }
    }

    if (target?.autoUnlockChallengesOffsetMinutes != null) {
      setAutoUnlockChallenges(true);
      setAutoUnlockChallengesMinutes(target?.autoUnlockChallengesOffsetMinutes.toString());
    }

    setSelectedStartDateAndTime();
    getSelectedDurationPreset();
  };

  const getSelectedDurationPreset = () => {
    if (eventTemplate) {
      const durationInfo = getEventDurationInfo(eventTemplate);

      if (durationInfo) {
        setNewDuration(durationInfo?.hours.toString());
      }

      return;
    }

    const duration = target?.duration;
    if (!duration?.hours && !duration?.minutes) {
      return;
    }

    if (durationItems[duration.hours] && duration.minutes === 0) {
      setNewDuration(duration.hours.toString());
    } else {
      const durationText = formatDuration(duration.hours, duration.minutes);

      setNewDuration('specific');
      setSelectedSpecificDuration(durationText);
    }
  };

  const setSelectedStartDateAndTime = () => {
    if (!target?.startDate || isNaN(new Date(target?.startDate).getTime())) {
      return;
    }

    // Polaris DatePicker component does not properly parse full startDate with time
    // Spliting here fixes default selection for DatePicker
    setStartDate(target?.startDate.split('T')[0]);

    const startTimeFromEvent = getTimeInTimeZone(target?.startDate, target?.timezone);
    const dateSelectorTime = getTimeForPolarisTimePicker(target?.startDate);

    if (startTimeFromEvent && dateSelectorTime) {
      setSelectedMeridiem(extractMeridiem(target?.startDate, target?.timezone ?? undefined));

      const convertedTime = convert24To12HourTime(startTimeFromEvent, false);
      setStartTime(convertedTime);
    }
  };

  const formatDuration = (hours: number, minutes: number) => {
    const hoursText = String(hours).padStart(2, '0');
    const minutesText = String(minutes).padStart(2, '0');

    return `${hoursText}:${minutesText}`;
  };

  const handleTimezoneSelection = (optionSelection: OptionDefinition) => {
    setSelectedTimezoneOption(optionSelection);
    handleUpdateEditEvent(EditEventActions.TIMEZONE, optionSelection.value);
  };

  const handleparticipantChange = (numOfparticipants: string) => {
    setParticipants(numOfparticipants);
    handleUpdateEditEvent(EditEventActions.MAX_EXPECTED_PARTICIPANTS, numOfparticipants);
  };

  const handleMinparticipantChange = (numOfparticipants: string) => {
    setMinParticipants(numOfparticipants);
    handleUpdateEditEvent(EditEventActions.MIN_EXPECTED_PARTICIPANTS, numOfparticipants);
  };

  const handleDurationChange = (updatedDuration: string) => {
    setNewDuration(updatedDuration);
    setSelectedSpecificDuration(null);
    setDurationError('');
  };

  const handleAutoUnlockChallengesCheck = (autoUnlockChallengesCheck: boolean) => {
    setAutoUnlockChallenges(autoUnlockChallengesCheck);
    setAutoUnlockChallengesMinutes('0');
    handleUpdateEditEvent(EditEventActions.AUTO_UNLOCK_CHALLENGES_MINUTES, !autoUnlockChallengesCheck ? null : 0);
  };

  const handleAutoUnlockChallengesMinutesChange = (selectedAutoUnlockChallengesMinutes: string) => {
    if (containsOnlyNumbers(selectedAutoUnlockChallengesMinutes)) {
      setAutoUnlockChallengesMinutes(selectedAutoUnlockChallengesMinutes);
      handleUpdateEditEvent(
        EditEventActions.AUTO_UNLOCK_CHALLENGES_MINUTES,
        Number(selectedAutoUnlockChallengesMinutes)
      );
    }
  };

  useEffect(() => {
    if (startBeforeEventApprovalSLA && tooSoonRef?.current) {
      tooSoonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [startBeforeEventApprovalSLA]);

  useEffect(() => {
    handleSetStartAndEndDateTimes();
  }, [
    startDateUpdated,
    startTimeUpdated,
    durationUpdated,
    startDate,
    startTime,
    newDuration,
    selectedSpecificDuration,
    selectedMeridiem,
    selectedTimezoneOption,
  ]);

  const handleSetStartAndEndDateTimes = () => {
    if (!validateFields() || !selectedTimezoneOption?.value) {
      return;
    }

    if (startMoment) {
      const durationValue = newDuration === 'specific' ? selectedSpecificDuration : newDuration;
      const duration = moment.duration(durationValue, 'hours');

      const endMoment = moment(startMoment).add(duration);

      handleUpdateEditEvent(EditEventActions.START_AND_END_DATETIMES, {
        startDateTime: startMoment.format(),
        endDateTime: endMoment.format(),
      });
    }
  };

  return (
    <SpaceBetween direction="vertical" size="s">
      {startBeforeEventApprovalSLA === 'ERROR' && (
        <div ref={tooSoonRef}>
          <Alert
            key="manual-approval-required-alert"
            data-testid="manual-approval-required-alert"
            type="error"
            header={t(i18nKeys.events.eventDetails.messages.eventApprovalSLAHeader)}
          >
            {t(i18nKeys.events.eventDetails.messages.eventApprovalSLAError, {
              requesterEventsCount,
              eventQuotaLimit,
              eventQuotaDaysPeriod,
              eventQuotaSLADays,
            })}
          </Alert>
        </div>
      )}
      {startBeforeEventApprovalSLA === 'WARNING' && (
        <Alert
          key="manual-approval-required-warning"
          data-testid="manual-approval-required-warning"
          type="warning"
          header={t(i18nKeys.events.eventDetails.messages.eventApprovalSLAHeader)}
        >
          {t(i18nKeys.events.eventDetails.messages.eventApprovalSLAWarning, {
            requesterEventsCount,
            eventQuotaDaysPeriod,
            eventQuotaLimit,
            eventQuotaSLADays,
          })}
        </Alert>
      )}
      <KeyValue label={<b>{t(i18nKeys.events.eventDetails.labels.timezone)}</b>} required>
        <div className="input-width-limit">
          <Select
            data-testid="timezone-select"
            selectedOption={selectedTimezoneOption}
            options={timezoneOptions}
            filteringType="auto"
            onChange={({ detail }) => {
              setStartTimeUpdated(true);
              handleTimezoneSelection(detail.selectedOption);
            }}
            disabled={isDisabled || afterStartTime}
            onBlur={() => handleSetStartAndEndDateTimes()}
          />
        </div>
      </KeyValue>
      <KeyValue
        label={<b>{t(i18nKeys.events.eventDetails.labels.startTime)}</b>}
        description={t(i18nKeys.events.eventDetails.messages.startTimeDescription)}
        required
      >
        <SpaceBetween direction="horizontal" size="m">
          <FormField
            i18nStrings={{ errorIconAriaLabel: t(i18nKeys.general.error) }}
            errorText={!afterStartTime && startDateError}
          >
            <DatePicker
              data-testid="date-picker"
              onChange={({ detail }) => {
                setStartDateUpdated(true);
                setStartDate(detail.value);
              }}
              value={startDate}
              openCalendarAriaLabel={(selectedDate) =>
                t(i18nKeys.general.chooseDate) +
                (selectedDate ? t(i18nKeys.general.selectedDate, { selectedDate }) : '')
              }
              disabled={afterStartTime}
              placeholder="YYYY/MM/DD"
              locale={languageCode}
              previousMonthAriaLabel={t(i18nKeys.events.fields.filters.labels.previousMonth)}
              nextMonthAriaLabel={t(i18nKeys.events.fields.filters.labels.nextMonth)}
              todayAriaLabel={t(i18nKeys.events.fields.filters.labels.today)}
              isDateEnabled={isDateNotInThePast}
              invalid={!afterStartTime && !!startDateError}
            />
          </FormField>
          <SpaceBetween direction="horizontal" size="xs">
            <FormField errorText={afterStartTime ? null : startTimeError}>
              <TimeInput
                value={startTime}
                placeholder="hh:mm"
                format="hh:mm"
                data-testid="time-picker"
                invalid={!!startTimeError}
                use24Hour={false}
                onChange={({ detail }) => {
                  setStartTimeUpdated(true);
                  setStartTime(detail.value);
                }}
                disabled={isDisabled || afterStartTime}
              />
            </FormField>
            <FormField>
              <Select
                options={[MeridiemOptions.AM, MeridiemOptions.PM]}
                onChange={({ detail }) => {
                  setStartTimeUpdated(true);
                  setSelectedMeridiem(detail.selectedOption);
                }}
                selectedOption={selectedMeridiem}
                invalid={!!startTimeError}
                disabled={isDisabled || afterStartTime}
                data-testid="amPmSelect"
              />
            </FormField>
          </SpaceBetween>
        </SpaceBetween>
      </KeyValue>
      <KeyValue label={t(i18nKeys.events.eventDetails.labels.duration)} bold required>
        <FormField
          i18nStrings={{ errorIconAriaLabel: t(i18nKeys.general.error) }}
          errorText={newDuration === 'specific' ? null : durationError}
        >
          <Tiles
            data-testid="event-duration-tiles"
            value={newDuration}
            onChange={({ detail }) => {
              setDurationUpdated(true);
              handleDurationChange(detail.value);
            }}
            items={Object.values(durationItems)}
          />
        </FormField>
      </KeyValue>
      {newDuration === 'specific' && (
        <KeyValue label={t(i18nKeys.events.eventDetails.labels.specificDuration)} bold required>
          <FormField errorText={afterStartTime ? null : durationError}>
            <SpaceBetween size="xs" direction="horizontal">
              <CustomTimeInput
                data-testid="time-specific-duration-input"
                value={selectedSpecificDuration || ''}
                placeholder="hh:mm"
                format="hh:mm"
                onChange={({ detail }) => {
                  setDurationUpdated(true);
                  setSelectedSpecificDuration(detail.value);
                }}
              />
            </SpaceBetween>
          </FormField>
        </KeyValue>
      )}
      <SpaceBetween size="xs" direction="horizontal" alignItems="center">
        <Checkbox
          data-testid="auto-unlock-checkbox"
          checked={autoUnlockChallenges}
          onChange={() => handleAutoUnlockChallengesCheck(!autoUnlockChallenges)}
          ariaLabel={t(i18nKeys.events.eventDetails.labels.autoUnlockChallenges)}
        />
        {t(i18nKeys.events.eventDetails.messages.automaticallyUnlockChallenges)}
        <Input
          data-testid="auto-unlock-challenges-minutes-input"
          disabled={!autoUnlockChallenges}
          inputMode="numeric"
          type="number"
          value={autoUnlockChallengesMinutes}
          onChange={({ detail }) => handleAutoUnlockChallengesMinutesChange(detail.value)}
          className="numeric-input"
          ariaLabel={t(i18nKeys.events.eventDetails.labels.numericUnlockChallengesMinutes)}
        />
        {t(i18nKeys.events.eventDetails.messages.minutesAfterStart)}
      </SpaceBetween>
      <div className="secondary-text">
        {autoUnlockChallenges &&
          autoUnlockChallengesMinutes === '0' &&
          t(i18nKeys.events.eventDetails.messages.autoUnlockChallengesInfo) +
            t(i18nKeys.events.eventDetails.messages.autoUnlockChallengesInfoEventStartTime)}

        {autoUnlockChallenges &&
          autoUnlockChallengesMinutes > '0' &&
          t(i18nKeys.events.eventDetails.messages.autoUnlockChallengesInfo) +
            autoUnlockChallengesMinutes +
            ' ' +
            t(i18nKeys.events.eventDetails.messages.autoUnlockChallengesInfoMinutesAfterEventStartTime)}

        {!autoUnlockChallenges && t(i18nKeys.events.eventDetails.messages.challengesWillRemainLocked)}
      </div>
      {!newEventMode && (
        <SpaceBetween direction="vertical" size="xs">
          {(target as Event)?.catalogId === null && (
            <KeyValue label={<b>{t(i18nKeys.challenges.challengeDetails.titles.minExpectedParticipants)}</b>} required>
              <SpaceBetween direction="horizontal" size="xs">
                <Input
                  data-testid="min-participants-input"
                  value={minParticipants ?? ''}
                  onChange={({ detail }) => handleMinparticipantChange(detail.value)}
                  disabled={isDisabled}
                />
              </SpaceBetween>
            </KeyValue>
          )}
          <KeyValue label={<b>{t(i18nKeys.events.fields.numberOfParticipants.participants)}</b>} required>
            <SpaceBetween direction="horizontal" size="xs">
              <Input
                data-testid="participants-input"
                value={participants ?? ''}
                onChange={({ detail }) => handleparticipantChange(detail.value)}
                disabled={isDisabled}
              />
            </SpaceBetween>
          </KeyValue>
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
};

export default EventTimes;
